// src/components/Cart/AddressInput.jsx - Simplified version
import { useState, useEffect, useCallback, useRef } from 'react';
import { getAddressSuggestions } from '../../api/api';
import { debounce } from '../../util';
import { useSharedAddress } from '../../hooks/useSharedAddress';

const AddressInput = ({ 
    setIsValidAddress, 
    cartData, 
    onAddressUpdate,
    isDisabled = false 
}) => {
    const [sharedAddress, setSharedAddress] = useSharedAddress();
    const [suggestions, setSuggestions] = useState([]);
    const addressInitialized = useRef(false);

    // Initialize address from cart data
    useEffect(() => {
        if (cartData?.address && !addressInitialized.current) {
            const { street1, city, state, zip } = cartData.address;
            if (city && cartData.address.deliverable) {
                const fullAddress = `${street1}, ${city}, ${state} ${zip}`;
                setSharedAddress(fullAddress);
                setIsValidAddress(true);
                addressInitialized.current = true;
            }
        }
    }, [cartData, setSharedAddress, setIsValidAddress]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSharedAddress(value);
        
        if (value === '') {
            setSuggestions([]);
            setIsValidAddress(false);
            // Clear address in checkout
            if (onAddressUpdate) {
                onAddressUpdate({
                    street1: '',
                    city: '',
                    state: '',
                    zip: '',
                });
            }
        } else {
            debouncedGetSuggestions(value);
        }
    };
    
    const debouncedGetSuggestions = useCallback(
        debounce(async (value) => {
            try {
                const response = await getAddressSuggestions(encodeURIComponent(value));
                if (response?.results) {
                    setSuggestions(response.results.map((result) => result.address));
                }
            } catch (error) {
                console.error('Failed to get address suggestions:', error);
                setSuggestions([]);
            }
        }, 300),
        []
    );

    const handleSuggestionClick = (suggestion) => {
        const newAddress = {
            street1: `${suggestion.streetNumber || ''} ${suggestion.streetName || ''}`.trim(),
            city: suggestion.municipality || '',
            state: suggestion.countrySubdivision || '',
            zip: suggestion.postalCode || '',
        };
        
        setSharedAddress(suggestion.freeformAddress);
        setSuggestions([]);
        setIsValidAddress(true);
        
        if (onAddressUpdate) {
            onAddressUpdate(newAddress);
        }
    };

    return (
        <div className='headless-cart-delivery-inputs'>
            {!sharedAddress ? (
                <div className='headless-cart-delivery-msg'>
                    Please enter your delivery address
                </div>
            ) : cartData?.address && cartData.address.city && !cartData.address.deliverable ? (
                <div className='headless-cart-delivery-msg'>
                    Address is outside delivery range!
                </div>
            ) : null}
            
            <input
                type='text'
                onChange={handleInputChange}
                value={sharedAddress}
                placeholder="Enter delivery address"
                disabled={isDisabled}
            />

            <ul
                className='headless-delivery-autofill'
                style={{ display: suggestions.length > 0 ? 'block' : 'none' }}
            >
                {suggestions.map((suggestion, i) => (
                    <li
                        key={i}
                        onClick={() => handleSuggestionClick(suggestion)}
                    >
                        {suggestion.freeformAddress}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default AddressInput;
